// @flow
import * as Immutable from 'immutable'; // This is required
import ImmutableModel from './ImmutableModel'; // Make sure you copied this file into your repo

import { Skills } from './Skills';
import { Team } from './Team';

import type { TeamModelType } from './Team';
import type { SkillsModelType } from './Skills';
import type { ZoneMapGeometryModelType } from './ZoneMapGeometry';

export type ExtendedUserModelType = {
    clientId: string,
    userId: string,
    name: string,
    email: string,
    phone: string,
    photoUrl: string,
    status: UserStatusWrapperModelType,
    teams: Array<TeamModelType>,
    /**
     * Routes is added to this model for displaying zones for suggested rider
     */
    routes: Array<ZoneMapGeometryModelType>,
    location: LocationInstantModelType,
    ongoingTasks: UserTaskInfoModelType,
    events: UserEventInfoModelType,
    skills: SkillsModelType,
    rating: number,
    effectiveStatus: UserEffectiveStatusModelType
};
export type UserStatusEnum = 'IDLE' | 'ACTIVE' | 'OFFLINE' | 'DISABLED';
export type UserBusyReasonEnum =
    | 'NONE'
    | 'BUSY_MANUAL'
    | 'LOGGED_OUT'
    | 'LOCATION_MISSING'
    | 'NEVER_LOGGED_IN';
export type UserEffectiveStatusEnum =
    | 'DISABLED'
    | 'BUSY'
    | 'ACTIVE_AND_IDLE'
    | 'ACTIVE_AND_EXECUTING';
export type CriticalEventTypeEnum =
    | 'USER_BATTERY_LEVEL'
    | 'USER_LOC_GAP'
    | 'TASK_ACCEPT_DELAY'
    | 'TASK_ETA_DELAY_COMPLETED'
    | 'TASK_SLA_DELAY_COMPLETED'
    | 'TASK_LOC_FAR_ARRIVED'
    | 'VEHICLE_FREEZER_TEMPERATURE'
    | 'TASK_TEMPERATURE_BREACH'
    | 'USER_HALT';

export type UserStatusWrapperModelType = {
    status: UserStatusEnum
};

export type UserEffectiveStatusModelType = {
    busyReason: UserBusyReasonEnum,
    effectiveStatus: UserEffectiveStatusEnum,
    isRelevantToday: boolean,
    updatedOn: string
};

export type LocationInstantModelType = {
    lat: number,
    lng: number,
    timestamp: string,
    speed: number
};

export type UserTaskInfoModelType = {
    count: number
};

export type UserEventInfoModelType = {
    count: number,
    criticalEvents: ?Array<CriticalEventModelType>
};

export type CriticalEventModelType = {
    type: CriticalEventTypeEnum,
    value: number
};

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class ExtendedUser extends ImmutableModel {
    static fromJS(json: ExtendedUserModelType): ExtendedUser {
        const state: Object = Object.assign({}, json);
        state.status = UserStatusWrapper.fromJS(state.status);
        state.teams = Immutable.List(state.teams).map(item =>
            Team.fromJS(item)
        );
        state.routes = Immutable.List(state.routes).map(item =>
            ZoneMapGeometry.fromJS(item)
        );
        state.location = LocationInstant.fromJS(state.location);
        state.ongoingTasks = UserTaskInfo.fromJS(state.ongoingTasks);
        state.events = UserEventInfo.fromJS(state.events);
        state.skills = Skills.fromJS(state.skills);
        state.effectiveStatus = UserEffectiveStatus.fromJS(
            state.effectiveStatus
        );
        return new this(Immutable.Map(state));
    }

    toJS(): ExtendedUserModelType {
        return {
            clientId: this.clientId,
            userId: this.userId,
            name: this.name,
            email: this.email,
            phone: this.phone,
            photoUrl: this.photoUrl,
            status: this.status.toJS(),
            teams: this.teams.toArray().map(item => item.toJS()),
            routes: this.routes.toArray().map(item => item.toJS()),
            location: this.location.toJS(),
            ongoingTasks: this.ongoingTasks.toJS(),
            events: this.events.toJS(),
            skills: this.skills.toJS(),
            rating: this.rating,
            effectiveStatus: this.effectiveStatus.toJS()
        };
    }

    get clientId(): string {
        return this._state.get('clientId');
    }

    setClientId(clientId: string): this {
        return this.clone(this._state.set('clientId', clientId));
    }

    get userId(): string {
        return this._state.get('userId');
    }

    setUserId(userId: string): this {
        return this.clone(this._state.set('userId', userId));
    }

    get name(): string {
        return this._state.get('name');
    }

    setName(name: string): this {
        return this.clone(this._state.set('name', name));
    }

    get email(): string {
        return this._state.get('email');
    }

    setEmail(email: string): this {
        return this.clone(this._state.set('email', email));
    }

    get phone(): string {
        return this._state.get('phone');
    }

    setPhone(phone: string): this {
        return this.clone(this._state.set('phone', phone));
    }

    get photoUrl(): string {
        return this._state.get('photoUrl');
    }

    setPhotoUrl(photoUrl: string): this {
        return this.clone(this._state.set('photoUrl', photoUrl));
    }

    get status(): UserStatusWrapper {
        return this._state.get('status');
    }

    setStatus(status: UserStatusWrapper): this {
        return this.clone(this._state.set('status', status));
    }

    get teams(): Immutable.List<Team> {
        return this._state.get('teams');
    }

    setTeams(teams: Immutable.List<Team>): this {
        return this.clone(this._state.set('teams', teams));
    }

    get routes(): Immutable.List<ZoneMapGeometry> {
        return this._state.get('routes');
    }

    setRoutes(routes: Immutable.List<ZoneMapGeometry>): this {
        return this.clone(this._state.set('routes', routes));
    }

    get location(): LocationInstant {
        return this._state.get('location');
    }

    setLocation(location: LocationInstant): this {
        return this.clone(this._state.set('location', location));
    }

    get ongoingTasks(): UserTaskInfo {
        return this._state.get('ongoingTasks');
    }

    setOngoingTasks(ongoingTasks: UserTaskInfo): this {
        return this.clone(this._state.set('ongoingTasks', ongoingTasks));
    }

    get events(): UserEventInfo {
        return this._state.get('events');
    }

    setEvents(events: UserEventInfo): this {
        return this.clone(this._state.set('events', events));
    }

    get skills(): Skills {
        return this._state.get('skills');
    }

    setSkills(skills: Skills): this {
        return this.clone(this._state.set('skills', skills));
    }

    get rating(): number {
        return this._state.get('rating');
    }

    setRating(rating: number): this {
        return this.clone(this._state.set('rating', rating));
    }

    get effectiveStatus(): UserEffectiveStatus {
        return this._state.get('effectiveStatus');
    }

    setEffectiveStatus(effectiveStatus: UserEffectiveStatus): this {
        return this.clone(this._state.set('effectiveStatus', effectiveStatus));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserStatusWrapper extends ImmutableModel {
    static fromJS(json: UserStatusWrapperModelType): UserStatusWrapper {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): UserStatusWrapperModelType {
        return {
            status: this.status
        };
    }

    get status(): UserStatusEnum {
        return this._state.get('status');
    }

    setStatus(status: UserStatusEnum): this {
        return this.clone(this._state.set('status', status));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class LocationInstant extends ImmutableModel {
    static fromJS(json: LocationInstantModelType): LocationInstant {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): LocationInstantModelType {
        return {
            lat: this.lat,
            lng: this.lng,
            timestamp: this.timestamp,
            speed: this.speed
        };
    }

    get lat(): number {
        return this._state.get('lat');
    }

    setLat(lat: number): this {
        return this.clone(this._state.set('lat', lat));
    }

    get lng(): number {
        return this._state.get('lng');
    }

    setLng(lng: number): this {
        return this.clone(this._state.set('lng', lng));
    }

    get timestamp(): string {
        return this._state.get('timestamp');
    }

    setTimestamp(timestamp: string): this {
        return this.clone(this._state.set('timestamp', timestamp));
    }

    get speed(): number {
        return this._state.get('speed');
    }

    setSpeed(speed: number): this {
        return this.clone(this._state.set('speed', speed));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserTaskInfo extends ImmutableModel {
    static fromJS(json: UserTaskInfoModelType): UserTaskInfo {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): UserTaskInfoModelType {
        return {
            count: this.count
        };
    }

    get count(): number {
        return this._state.get('count');
    }

    setCount(count: number): this {
        return this.clone(this._state.set('count', count));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserEventInfo extends ImmutableModel {
    static fromJS(json: UserEventInfoModelType): UserEventInfo {
        const state: Object = Object.assign({}, json);
        state.criticalEvents = state.criticalEvents
            ? Immutable.List(state.criticalEvents).map(item =>
                  CriticalEvent.fromJS(item)
              )
            : state.criticalEvents;
        return new this(Immutable.Map(state));
    }

    toJS(): UserEventInfoModelType {
        return {
            count: this.count,
            criticalEvents: this.criticalEvents
                ? this.criticalEvents.toArray().map(item => item.toJS())
                : this.criticalEvents
        };
    }

    get count(): number {
        return this._state.get('count');
    }

    setCount(count: number): this {
        return this.clone(this._state.set('count', count));
    }

    get criticalEvents(): ?Immutable.List<CriticalEvent> {
        return this._state.get('criticalEvents');
    }

    setCriticalEvents(criticalEvents: ?Immutable.List<CriticalEvent>): this {
        return this.clone(this._state.set('criticalEvents', criticalEvents));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class CriticalEvent extends ImmutableModel {
    static fromJS(json: CriticalEventModelType): CriticalEvent {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): CriticalEventModelType {
        return {
            type: this.type,
            value: this.value
        };
    }

    get type(): CriticalEventTypeEnum {
        return this._state.get('type');
    }

    setType(type: CriticalEventTypeEnum): this {
        return this.clone(this._state.set('type', type));
    }

    get value(): number {
        return this._state.get('value');
    }

    setValue(value: number): this {
        return this.clone(this._state.set('value', value));
    }
}

// /////////////////////////////////////////////////////////////////////////////
//
// NOTE: THIS CLASS IS GENERATED. DO NOT MAKE CHANGES HERE.
//
// If you need to update this class, update the corresponding flow type above
// and re-run the flow-immutable-models codemod
//
// /////////////////////////////////////////////////////////////////////////////
export class UserEffectiveStatus extends ImmutableModel {
    static fromJS(json: UserEffectiveStatusModelType): UserEffectiveStatus {
        const state: Object = Object.assign({}, json);
        return new this(Immutable.Map(state));
    }

    toJS(): UserEffectiveStatusModelType {
        return {
            busyReason: this.busyReason,
            effectiveStatus: this.effectiveStatus,
            isRelevantToday: this.isRelevantToday,
            updatedOn: this.updatedOn
        };
    }

    get busyReason(): UserBusyReasonEnum {
        return this._state.get('busyReason');
    }

    setBusyReason(busyReason: UserBusyReasonEnum): this {
        return this.clone(this._state.set('busyReason', busyReason));
    }

    get effectiveStatus(): UserEffectiveStatusEnum {
        return this._state.get('effectiveStatus');
    }

    setEffectiveStatus(effectiveStatus: UserEffectiveStatusEnum): this {
        return this.clone(this._state.set('effectiveStatus', effectiveStatus));
    }

    get isRelevantToday(): boolean {
        return this._state.get('isRelevantToday');
    }

    setIsRelevantToday(isRelevantToday: boolean): this {
        return this.clone(this._state.set('isRelevantToday', isRelevantToday));
    }

    get updatedOn(): string {
        return this._state.get('updatedOn');
    }

    setUpdatedOn(updatedOn: string): this {
        return this.clone(this._state.set('updatedOn', updatedOn));
    }
}
